import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Card, TabBar } from '@thegpvc/ui';
import { api } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { PageContainer } from '../components/PageContainer';
import { TimeSeriesChart } from '../components/TimeSeriesChart';
import { UsageView } from '../components/UsageView';
import { InfoHint } from '../components/InfoHint';
import { CHART_COLORS } from '../styles/chartColors';
import { text, sectionTitle, cx } from '../styles/theme';
import type { TimeGranularity, TimePeriod, TimeSeriesDataPoint } from '../types/api';
import { formatDuration, formatNumber } from '../utils/format';
import { errorMessage } from '../utils/errorMessage';
import {
  aggregateTokenSeries,
  aggregateTokenByModelSeries,
  emptyTokenTotals,
} from '../utils/tokenBucketing';
import { aggregateActivitySeries, emptyActivityTotals } from '../utils/activityBucketing';
import { modelColors } from '../styles/chartRamp';
import {
  CONTENT_TOKENS_TOOLTIP,
  CACHE_TOKENS_TOOLTIP,
  TOTAL_TOKENS_TOOLTIP,
  COST_TOKENS_TOOLTIP,
} from '../utils/tokenLabels';

const views = [
  { id: 'trends', label: 'Trends' },
  { id: 'usage', label: 'Usage' },
];

const periods = [
  { id: '7days', label: '7d' },
  { id: '30days', label: '30d' },
  { id: 'all', label: 'all' },
];

// The 7d/30d/all range switcher (#630 §2): the `.gp-tabs-underline` idiom — a
// 2px underline that turns orange on the selected pill. aria-selected is wired
// so the CSS styles off it; the same range value keys UsageView's single query.
function RangeTabs({ value, onChange }: { value: TimePeriod; onChange: (id: string) => void }) {
  return (
    <div className="gp-tabs-underline" role="tablist" aria-label="Time range">
      {periods.map((p) => (
        <button
          key={p.id}
          type="button"
          role="tab"
          aria-selected={value === p.id}
          className="gp-tab-underline"
          onClick={() => onChange(p.id)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// The two behavioral charts measure the same ratios that the Autonomy tile on
// the session detail page explains (see #547), but aggregated over time. Both
// are otherwise opaque numbers, so — like the token charts — they carry help
// text on the header describing exactly what they measure and which direction
// is "more".
const AUTONOMY_TOOLTIP =
  'Autonomy = AI turns ÷ (user + AI turns). The line is the median session in each bucket; the shaded band spans the 10th–90th percentiles. Higher means more of the work ran AI-driven between prompts.';

const AI_TURNS_PER_USER_TURN_TOOLTIP =
  'Average AI (assistant) turns per user prompt in each bucket. Higher means longer autonomous stretches between your messages.';

// The #362 replacement for the saturating autonomy-median trend: a "stretch" is
// a run of consecutive tool calls between prompts, and this plots the median
// session's longest stretch (max_stretch) in each bucket.
const MEDIAN_MAX_STRETCH_TOOLTIP =
  'Median across sessions of each session’s longest stretch (a run of consecutive tool calls between your prompts) in each bucket. The number is a tool-call count. Higher means longer autonomous runs.';

// The by-model breakdown sums the same total-token figure as the "Total
// Tokens" card (input + output + cache_creation + cache_read, cache included)
// so the two reconcile — just split per model instead of one combined bar.
const SESSIONS_TOOLTIP =
  'Sessions with agent activity in each period. A session that runs for several days counts on every day it was active.';
const DURATION_TOOLTIP =
  'Active agent time in each period, summed across sessions (parallel sessions add up). Idle time while a session sat open is not counted.';
const TOTAL_TOKENS_BY_MODEL_TOOLTIP =
  'Total tokens per day split by model, stacked. Total = input + output + cache creation + cache read (cache included), so the stack sums to the same figure as the "Total Tokens" card.';

const granularities = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
];

function defaultGranularity(period: TimePeriod): TimeGranularity {
  switch (period) {
    case '30days':
      return 'weekly';
    case 'all':
      return 'monthly';
    default:
      return 'daily';
  }
}

// One Trends chart tile: a Card with a section-title header — plus an optional
// focusable InfoHint help affordance — wrapping the chart body. Collapses the
// Card/Card.Header/Card.Body boilerplate each chart repeated identically.
function ChartCard({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-1.5">
          <h3 className={sectionTitle}>{title}</h3>
          {hint && <InfoHint label={hint} />}
        </div>
      </Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
}

export function Stats() {
  // View is URL-driven (?view=usage) so Home links can land directly on it.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'usage' ? 'usage' : 'trends';
  const [period, setPeriod] = useState<TimePeriod>('30days');
  const [granularity, setGranularity] = useState<TimeGranularity>(() => defaultGranularity('30days'));

  const handlePeriodChange = (id: string) => {
    const p = id as TimePeriod;
    setPeriod(p);
    setGranularity(defaultGranularity(p));
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['timeseries', period, granularity],
    queryFn: () => api.getTimeSeries(period, granularity),
    placeholderData: keepPreviousData,
  });

  // Token figures come from a separate, always-daily, time-correct series
  // (session_token_usage_daily rollup) rather than the start-bucketed
  // timeseries above — see #443. Aggregated client-side to match the
  // selected granularity.
  const { data: tokenData } = useQuery({
    queryKey: ['tokenseries', period],
    queryFn: () => api.getTokenSeries(period),
    placeholderData: keepPreviousData,
  });

  const tokenBuckets = useMemo(
    () => aggregateTokenSeries(tokenData?.dataPoints ?? [], granularity),
    [tokenData, granularity]
  );

  // Turns/tool-calls figures come from a separate, always-daily, time-correct
  // series (session_activity_daily rollup) rather than the start-bucketed
  // timeseries above — mirrors the token series fix. Aggregated client-side
  // to match the selected granularity.
  const { data: activityData } = useQuery({
    queryKey: ['activityseries', period],
    queryFn: () => api.getActivitySeries(period),
    placeholderData: keepPreviousData,
  });

  const activityBuckets = useMemo(
    () => aggregateActivitySeries(activityData?.dataPoints ?? [], granularity),
    [activityData, granularity]
  );

  // Per-day, per-model token totals (session_model_tokens_daily rollup), long
  // format, pivoted client-side into per-bucket stacked series for the "Total
  // Tokens by Model" card. Always-daily and time-correct like the token series
  // above; aggregated client-side to the selected granularity.
  const { data: tokenByModelData } = useQuery({
    queryKey: ['tokenbymodelseries', period],
    queryFn: () => api.getTokenSeriesByModel(period),
    placeholderData: keepPreviousData,
  });

  const tokenByModelBuckets = useMemo(
    () => aggregateTokenByModelSeries(tokenByModelData?.dataPoints ?? [], granularity),
    [tokenByModelData, granularity]
  );

  // Transform data points: convert string int64 fields to numbers, and
  // overlay the time-correct token and activity totals for the same bucket.
  //
  // The bucket axis is the UNION of the primary (session-start-bucketed)
  // series and the token/activity (consumption/occurrence-day-bucketed)
  // series — not just the former. A long-running session with no *new*
  // session starting in the selected window would otherwise produce zero rows
  // in the primary series for that window, silently hiding real token
  // consumption or tool-call/turn activity for exactly the scenario this fix
  // targets (see #443/#441, and the activity-rollup follow-up).
  const chartData = useMemo(() => {
    const otherByBucket = new Map<string, TimeSeriesDataPoint>();
    for (const dp of data?.dataPoints ?? []) {
      otherByBucket.set(dp.bucket, dp);
    }

    // The activity rollup (session_activity_daily) is populated by reprocessing;
    // until a backfill has run, GetActivitySeries returns nothing. In that window
    // fall back to the start-bucketed turns/tool_calls from GetTimeSeries so the
    // charts show existing data rather than going empty. Once the rollup has any
    // data, switch to the time-correct occurrence-day series.
    const hasActivityRollup = (activityData?.dataPoints?.length ?? 0) > 0;

    const buckets = new Set<string>([
      ...otherByBucket.keys(),
      ...tokenBuckets.keys(),
      ...activityBuckets.keys(),
    ]);

    return Array.from(buckets)
      .sort()
      .map((bucket) => {
        const dp = otherByBucket.get(bucket);
        const tokens = tokenBuckets.get(bucket) ?? emptyTokenTotals;
        const activity = activityBuckets.get(bucket) ?? emptyActivityTotals;
        return {
          bucket,
          sessions: parseInt(dp?.sessions || '0', 10),
          turns: hasActivityRollup ? activity.userTurns : parseInt(dp?.turns || '0', 10),
          toolCalls: hasActivityRollup ? activity.toolCalls : parseInt(dp?.toolCalls || '0', 10),
          durationMs: parseInt(dp?.durationMs || '0', 10),
          autonomy: dp?.autonomy || 0,
          autonomyP10: dp?.autonomyP10 || 0,
          autonomyP90: dp?.autonomyP90 || 0,
          aiTurnsPerUserTurn: dp?.aiTurnsPerUserTurn || 0,
          medianMaxStretch: dp?.medianMaxStretch || 0,
          inputTokens: tokens.inputTokens,
          outputTokens: tokens.outputTokens,
          cacheCreationTokens: tokens.cacheCreationTokens,
          cacheReadTokens: tokens.cacheReadTokens,
          costTokens: tokens.costTokens,
          totalTokens: tokens.totalTokens,
        };
      });
  }, [data, tokenBuckets, activityBuckets, activityData]);

  // Build the dynamic model series (sorted by total tokens desc, coloured by
  // provider family — Claude orange, OpenAI navy — via modelColors, sharing a stackId) and the pivoted per-bucket rows for the "Total
  // Tokens by Model" stacked bar. The bucket axis is the union with the main
  // chart so the card lines up on the shared x-axis. When no model has any
  // tokens in the period (subscription/unpriced periods, or a filter that
  // selects nothing) modelSeries is empty and the card shows an explicit
  // "No data" state rather than an empty axis.
  const { modelSeries, modelChartData } = useMemo(() => {
    const totals = new Map<string, number>();
    for (const byModel of tokenByModelBuckets.values()) {
      for (const [model, value] of byModel) {
        totals.set(model, (totals.get(model) ?? 0) + value);
      }
    }
    const models = Array.from(totals.entries())
      .filter(([, value]) => value > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([model]) => model);

    const colors = modelColors(models);
    const series = models.map((model) => ({
      key: model,
      color: colors.get(model) ?? '',
      name: model,
      stackId: 'model',
    }));

    const buckets = new Set<string>([
      ...chartData.map((r) => r.bucket),
      ...tokenByModelBuckets.keys(),
    ]);
    const rows = Array.from(buckets)
      .sort()
      .map((bucket) => {
        const byModel = tokenByModelBuckets.get(bucket);
        const row: Record<string, number | string> = { bucket };
        for (const model of models) {
          row[model] = byModel?.get(model) ?? 0;
        }
        return row;
      });

    return { modelSeries: series, modelChartData: rows };
  }, [tokenByModelBuckets, chartData]);

  if (error) {
    return (
      <PageContainer>
        <title>Dixie :: Stats</title>
        <PageHeader title="Stats" />
        <Alert variant="error">Failed to load stats: {errorMessage(error)}</Alert>
      </PageContainer>
    );
  }

  const isEmpty = !isLoading && chartData.length === 0;

  return (
    <PageContainer>
      <title>Dixie :: Stats</title>
      <PageHeader title="Stats" isFetching={isFetching && !isLoading}>
        <TabBar
          variant="pill"
          items={views}
          activeId={view}
          onChange={(id: string) =>
            setSearchParams(id === 'trends' ? {} : { view: id }, { replace: true })
          }
        />
        <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
        {view === 'trends' && (
          <>
            <TabBar
              items={granularities}
              activeId={granularity}
              onChange={(id) => setGranularity(id as TimeGranularity)}
            />
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
          </>
        )}
        <RangeTabs value={period} onChange={handlePeriodChange} />
      </PageHeader>

      {view === 'usage' && <UsageView period={period} />}
      {view === 'trends' && (
      <>

      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <Card key={i}>
              <Card.Header><div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" /></Card.Header>
              <Card.Body><div className="h-60 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" /></Card.Body>
            </Card>
          ))}
        </div>
      )}

      {isEmpty && (
        <Card>
          <Card.Body>
            <p className={cx('text-center py-8', text.secondary)}>No data available for the selected period.</p>
          </Card.Body>
        </Card>
      )}

      {!isLoading && chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Sessions" hint={SESSIONS_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[{ key: 'sessions', color: CHART_COLORS.orange, name: 'Sessions' }]}
              tooltipFormatter={(v) => formatNumber(v)}
            />
          </ChartCard>

          <ChartCard title="Turns">
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[{ key: 'turns', color: CHART_COLORS.blue, name: 'Turns' }]}
              tooltipFormatter={(v) => formatNumber(v)}
            />
          </ChartCard>

          <ChartCard title="Tool Calls">
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[{ key: 'toolCalls', color: CHART_COLORS.emerald, name: 'Tool Calls' }]}
              tooltipFormatter={(v) => formatNumber(v)}
            />
          </ChartCard>

          <ChartCard title="Content Tokens" hint={CONTENT_TOKENS_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[
                { key: 'inputTokens', color: CHART_COLORS.purple, name: 'Input Tokens', stackId: 'content' },
                { key: 'outputTokens', color: CHART_COLORS.cyan, name: 'Output Tokens', stackId: 'content' },
              ]}
              yFormatter={(v) => formatNumber(v)}
              tooltipFormatter={(v) => formatNumber(v)}
            />
          </ChartCard>

          <ChartCard title="Cache Tokens" hint={CACHE_TOKENS_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[
                { key: 'cacheReadTokens', color: CHART_COLORS.emerald, name: 'Cache Read', stackId: 'cache' },
                { key: 'cacheCreationTokens', color: CHART_COLORS.amber, name: 'Cache Write', stackId: 'cache' },
              ]}
              yFormatter={(v) => formatNumber(v)}
              tooltipFormatter={(v) => formatNumber(v)}
            />
          </ChartCard>

          <ChartCard title="Total Tokens" hint={TOTAL_TOKENS_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[
                { key: 'totalTokens', color: CHART_COLORS.amber, name: 'Total Tokens' },
              ]}
              yFormatter={(v) => formatNumber(v)}
              tooltipFormatter={(v) => formatNumber(v)}
            />
          </ChartCard>

          <ChartCard title="Total Tokens by Model" hint={TOTAL_TOKENS_BY_MODEL_TOOLTIP}>
            {modelSeries.length === 0 ? (
              <p className={cx('text-center py-8', text.secondary)}>
                No model token data for the selected period.
              </p>
            ) : (
              <TimeSeriesChart
                syncId="stats"
                data={modelChartData}
                series={modelSeries}
                yFormatter={(v) => formatNumber(v)}
                tooltipFormatter={(v) => formatNumber(v)}
              />
            )}
          </ChartCard>

          <ChartCard title="Cost Tokens" hint={COST_TOKENS_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[{ key: 'costTokens', color: CHART_COLORS.rose, name: 'Cost Tokens' }]}
              yFormatter={(v) => formatNumber(v)}
              tooltipFormatter={(v) => formatNumber(v)}
            />
          </ChartCard>

          <ChartCard title="Duration" hint={DURATION_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[{ key: 'durationMs', color: CHART_COLORS.orange, name: 'Duration' }]}
              yFormatter={(v) => formatDuration(v)}
              tooltipFormatter={(v) => formatDuration(v)}
            />
          </ChartCard>

          <ChartCard title="Autonomy (Median)" hint={AUTONOMY_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[
                { key: 'autonomyP10', color: CHART_COLORS.blue + '44', name: 'p10' },
                { key: 'autonomy', color: CHART_COLORS.blue, name: 'Median' },
                { key: 'autonomyP90', color: CHART_COLORS.blue + '44', name: 'p90' },
              ]}
              type="line"
              yFormatter={(v) => `${(v * 100).toFixed(0)}%`}
              tooltipFormatter={(v) => `${(v * 100).toFixed(1)}%`}
            />
          </ChartCard>

          <ChartCard title="Max Stretch (Median, tool calls)" hint={MEDIAN_MAX_STRETCH_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[{ key: 'medianMaxStretch', color: CHART_COLORS.purple, name: 'Max Stretch (Median)' }]}
              type="line"
              yFormatter={(v) => v.toFixed(0)}
              tooltipFormatter={(v) => v.toFixed(1)}
            />
          </ChartCard>

          <ChartCard title="AI Turns / User Turn" hint={AI_TURNS_PER_USER_TURN_TOOLTIP}>
            <TimeSeriesChart
              syncId="stats"
              data={chartData}
              series={[{ key: 'aiTurnsPerUserTurn', color: CHART_COLORS.emerald, name: 'AI Turns / User Turn' }]}
              type="line"
              yFormatter={(v) => v.toFixed(1)}
              tooltipFormatter={(v) => v.toFixed(2)}
            />
          </ChartCard>
        </div>
      )}
      </>
      )}
    </PageContainer>
  );
}
